import { useEffect, useRef } from 'react';

interface Props {
  hasMore: boolean;
  loading: boolean;
  onReach: () => void;
}

/**
 * Stage 12 (C4): invisible sentinel placed at the end of a gallery list.
 * When it enters the viewport (with a small rootMargin so the user doesn't
 * hit a hard bottom), it calls `onReach` — the gallery fires the next-
 * page request.
 */
export function LoadMoreSentinel({ hasMore, loading, onReach }: Props): JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !loading) {
            onReach();
          }
        }
      },
      { rootMargin: '200px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, onReach]);

  if (!hasMore) return null;

  return (
    <div className="gallery-sentinel" ref={ref}>
      {loading ? (
        <span className="muted small">Loading more…</span>
      ) : (
        <span className="muted small">Scroll for more</span>
      )}
    </div>
  );
}
